import { useEffect, useState } from "react";
import { Plus, Pencil, Users } from "lucide-react";
import { toast } from "sonner";
import api, { fmtApiError } from "@/lib/adminApi";
import { Badge, inputCls, SUBJECT_OPTIONS } from "./shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { name: "", subjects: [], qualification: "", experience: "", status: "active" };

export default function AdminTeachers() {
    const [items, setItems] = useState(null);
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/teachers").then((r) => setItems(r.data.items)).catch(() => toast.error("Could not load teachers"));
    useEffect(() => { load(); }, []);

    const openDialog = (t) => {
        setForm(t ? { ...empty, ...t } : empty);
        setDialog({ open: true, initial: t });
    };
    const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
    const toggleSubject = (s) =>
        setForm({ ...form, subjects: form.subjects.includes(s) ? form.subjects.filter((x) => x !== s) : [...form.subjects, s] });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (dialog.initial?.id) await api.patch(`/teachers/${dialog.initial.id}`, form);
            else await api.post("/teachers", form);
            toast.success(dialog.initial ? "Teacher updated" : "Teacher added");
            setDialog({ open: false, initial: null });
            load();
        } catch (err) {
            toast.error(fmtApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-teachers">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-serif text-3xl font-semibold text-ink">Teachers</h1>
                    <p className="mt-1 text-sm text-muted-foreground">Your teaching team and their students</p>
                </div>
                <button onClick={() => openDialog(null)} data-testid="add-teacher-button"
                    className="flex items-center gap-2 rounded-full bg-saffron px-6 py-2.5 text-sm font-medium text-cream transition-colors hover:bg-teal">
                    <Plus className="h-4 w-4" /> Add Teacher
                </button>
            </div>

            {!items || items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-white p-12 text-center text-sm text-muted-foreground" data-testid="teachers-empty">
                    No teachers yet. Add Rashmi, Rituraj and Ragini to start assigning students.
                </div>
            ) : (
                <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="teachers-grid">
                    {items.map((t) => (
                        <div key={t.id} className="rounded-2xl border border-border bg-white p-6 card-shadow" data-testid={`teacher-admin-card-${t.name.toLowerCase().replace(/\s/g, "-")}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="font-serif text-xl font-semibold text-ink">{t.name}</h3>
                                    <p className="mt-1 text-xs text-muted-foreground">{t.qualification || "—"}</p>
                                </div>
                                <Badge value={t.status} />
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {(t.subjects || []).map((s) => (
                                    <span key={s} className="rounded-full bg-teal-soft px-3 py-1 text-xs font-medium text-teal">{s}</span>
                                ))}
                            </div>
                            <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Users className="h-4 w-4 text-teal" /> {t.student_count} active student{t.student_count === 1 ? "" : "s"}
                                </span>
                                <button onClick={() => openDialog(t)} data-testid={`edit-teacher-${t.id}`}
                                    className="flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs font-medium text-ink hover:bg-sand">
                                    <Pencil className="h-3 w-3" /> Edit
                                </button>
                            </div>
                            {t.experience && <p className="mt-3 text-xs text-muted-foreground">{t.experience}</p>}
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog({ open: false, initial: null })}>
                <DialogContent data-testid="teacher-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">{dialog.initial ? "Edit Teacher" : "Add Teacher"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="mt-2 space-y-4">
                        <div className="space-y-1.5">
                            <Label>Name *</Label>
                            <Input required value={form.name} onChange={set("name")} className={inputCls} data-testid="teacher-name-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Subjects</Label>
                            <div className="flex flex-wrap gap-2">
                                {SUBJECT_OPTIONS.map((s) => (
                                    <button type="button" key={s} onClick={() => toggleSubject(s)} data-testid={`teacher-subject-${s.toLowerCase()}`}
                                        className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                                            form.subjects.includes(s) ? "border-teal bg-teal text-cream" : "border-border bg-white text-ink/70 hover:border-teal"
                                        }`}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label>Qualification</Label>
                                <Input value={form.qualification} onChange={set("qualification")} className={inputCls} data-testid="teacher-qualification-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Experience</Label>
                                <Input value={form.experience} onChange={set("experience")} className={inputCls} placeholder="7 years online" data-testid="teacher-experience-input" />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Status</Label>
                            <Select value={form.status} onValueChange={set("status")}>
                                <SelectTrigger className={inputCls} data-testid="teacher-status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex justify-end gap-3">
                            <button type="button" onClick={() => setDialog({ open: false, initial: null })} className="rounded-full border border-border px-6 py-2.5 text-sm font-medium">Cancel</button>
                            <button type="submit" disabled={saving} className="rounded-full bg-saffron px-6 py-2.5 text-sm font-medium text-cream hover:bg-teal disabled:opacity-60" data-testid="teacher-save-button">
                                {saving ? "Saving…" : "Save Teacher"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
